import {TOWN_REPORTS} from '../data/townReportNavigation';
import {useSharedFilter} from '../../../shared/navigation/useSharedReport';
import { TableSearchScope, SectionTableSearch } from "../../../shared/ui/table/TableSearchScope";
// src/features/townBudget/components/SummaryOfRevenuesAndExpenditures.jsx

import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import { CommonTable } from "../../../shared/ui/table/CommonTable.jsx";
import { buildRevenueColumns } from "../../../budget/model/columns/columnBuilders.js";
import { fetchRevenueData } from "../data/fetchRevenueData.js";
import { fetchExpenditureSections } from "../data/fetchExpenditureSections.js";
import { PieChartWithToggles } from "../../../shared/ui/chart/PieChartWithToggles.jsx";
import { HeaderToggleButton } from "../../../shared/ui/overlay/HeaderToggleButton.jsx";
import { LineChartWithToggles } from "../../../shared/ui/chart/LineChartWithToggles.jsx";
import {
  REVENUE_CATEGORY_CONFIG,
  TOWN_CATEGORY_LABELS,
  computeIncreaseFields,
  aggregateFromSections,
  aggregateFromSingleSection,
  rowHasAnyNumeric,
} from "../utils/summaryOfRevenuesAndExpendituresUtils.js";

/**
 * Town years are now keyed by START year.
 * startYear=2025 => FY 2025–2026
 */
function fyLabelFromStartYear(startYear) {
  if (!Number.isFinite(startYear)) return "";
  return `${startYear}–${startYear + 1}`;
}

export function SummaryOfRevenuesAndExpenditures({ availableYears = [] }) {
  const [collapsed, setCollapsed] = useState(true);

  const [loading, setLoading] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  const [tableYear, setTableYear] = useSharedFilter("SummaryOfRevenuesAndExpenditures.jsx:SummaryOfRevenuesAndExpenditures:tableYear", null);

  const [revenueRows, setRevenueRows] = useState([]);
  const [townRows, setTownRows] = useState([]);
  const [debtRows, setDebtRows] = useState([]);
  const [educationRows, setEducationRows] = useState([]);
  const [totalAppropsRows, setTotalAppropsRows] = useState([]);

  const [validYears, setValidYears] = useState([]);

  // --- Pie chart UI state ---
  const [showSpendPie, setShowSpendPie] = useState(false);
  const [pieColKey, setPieColKey] = useSharedFilter("SummaryOfRevenuesAndExpenditures.jsx:SummaryOfRevenuesAndExpenditures:pieColKey", "y0_adopted");

  // --- Totals charts UI state ---
  const [showApprovedTotalsChart, setShowApprovedTotalsChart] = useState(false);
  const [showActualTotalsChart, setShowActualTotalsChart] = useState(false);
  const [showDiffTotalsChart, setShowDiffTotalsChart] = useState(false);

  const [isChartUpdating, setIsChartUpdating] = useState(false);

  const yearCacheRef = useRef(new Map());

  const hasAnyData =
    revenueRows.length ||
    townRows.length ||
    debtRows.length ||
    educationRows.length ||
    totalAppropsRows.length;

  // Define year dropdown options early
  const yearOptionsToShow = useMemo(
    () => (validYears.length ? validYears : availableYears),
    [validYears, availableYears]
  );

  // For completeness checks: require all base columns (excluding label)
  const requiredBaseKeys = useMemo(() => {
    const sampleYear =
      availableYears?.[availableYears.length - 1] ?? new Date().getFullYear();

    const { baseColumns } = buildRevenueColumns(sampleYear);

    return (baseColumns || [])
      .map((c) => c?.key)
      .filter((k) => k && k !== "label");
  }, [availableYears]);

  const isFiniteNumber = (v) => typeof v === "number" && Number.isFinite(v);

  const isYearComplete = useCallback(
    (computed) => {
      if (!computed) return false;

      const totalRevenuesRow =
        (computed.revenueRows || []).find((r) => r?.label === "TOTAL REVENUES") ||
        null;

      const totalAppropsRow = (computed.totalAppropsRows || [])[0] || null;

      if (!totalRevenuesRow || !totalAppropsRow) return false;

      return requiredBaseKeys.every(
        (k) =>
          isFiniteNumber(totalRevenuesRow[k]) && isFiniteNumber(totalAppropsRow[k])
      );
    },
    [requiredBaseKeys]
  );

  const computeSummaryForYear = useCallback(async (startYear) => {
    const { columns: yearColumns, baseColumns: yearBaseColumns } =
      buildRevenueColumns(startYear);

    // 1) REVENUES
    const [taxes, licenses, intergov, charges, other, transfers] =
      await Promise.all([
        fetchRevenueData("taxes", yearBaseColumns),
        fetchRevenueData("licensesAndPermits", yearBaseColumns),
        fetchRevenueData("intergovernmental", yearBaseColumns),
        fetchRevenueData("chargesForServices", yearBaseColumns),
        fetchRevenueData("otherRevenues", yearBaseColumns),
        fetchRevenueData("transfersIn", yearBaseColumns),
      ]);

    // 2) EXPENDITURES
    // IMPORTANT FIX:
    // Use the same canonical path as SummaryOfExpenditures:
    // education comes from fetchExpenditureSections("education", ...)
    const [town, debtServiceAndTransfers, education] = await Promise.all([
      fetchExpenditureSections("town", yearBaseColumns),
      fetchExpenditureSections("debtServiceAndTransfers", yearBaseColumns),
      fetchExpenditureSections("education", yearBaseColumns),
    ]);

    // ---------- REVENUES TABLE ----------
    const revenueSectionsByKey = {
      taxes: taxes.sections || [],
      licensesAndPermits: licenses.sections || [],
      intergovernmental: intergov.sections || [],
      chargesForServices: charges.sections || [],
      otherRevenues: other.sections || [],
      transfersIn: transfers.sections || [],
    };

    const revenues = [];
    let revOrder = 0;

    revenues.push({ section: "REVENUES", order: revOrder++ });

    for (const cfg of REVENUE_CATEGORY_CONFIG) {
      const sectionsForCat = revenueSectionsByKey[cfg.key] || [];
      const aggRow = aggregateFromSections(sectionsForCat, yearColumns, cfg.label);
      if (rowHasAnyNumeric(aggRow, yearColumns)) {
        aggRow.order = revOrder++;
        revenues.push(aggRow);
      }
    }

    const totalRevenuesRow = (() => {
      const row = { label: "TOTAL REVENUES", bold: true };
      const yearCols = yearColumns.filter(
        (c) => !["label", "increase", "percentIncrease"].includes(c.key)
      );

      yearCols.forEach((col) => {
        let sum = 0;
        let hasVal = false;

        for (const r of revenues) {
          if (r.section) continue;
          const v = r[col.key];
          if (typeof v === "number" && Number.isFinite(v)) {
            sum += v;
            hasVal = true;
          }
        }

        if (hasVal) row[col.key] = sum;
      });

      return computeIncreaseFields(row);
    })();
    totalRevenuesRow.order = revOrder++;
    revenues.push(totalRevenuesRow);

    // ---------- EXPENDITURES TABLES ----------
    const townSectionsByName = {};
    (town.sections || []).forEach((section) => {
      if (section?.name) townSectionsByName[section.name] = section;
    });

    // --- TOWN rows ---
    const townTableRows = [];
    let townOrder = 0;

    townTableRows.push({
      section: "APPROPRIATIONS/EXPENDITURES – TOWN",
      order: townOrder++,
    });

    const townCategoryRows = [];
    for (const label of TOWN_CATEGORY_LABELS) {
      const section = townSectionsByName[label];
      if (!section) continue;

      const aggRow = aggregateFromSingleSection(section, yearColumns, label);
      if (aggRow && rowHasAnyNumeric(aggRow, yearColumns)) {
        aggRow.order = townOrder++;
        townCategoryRows.push(aggRow);
        townTableRows.push(aggRow);
      }
    }

    const totalTownRow = (() => {
      const row = { label: "TOTAL TOWN", bold: true };
      const yearCols = yearColumns.filter(
        (c) => !["label", "increase", "percentIncrease"].includes(c.key)
      );

      yearCols.forEach((col) => {
        let sum = 0;
        let hasVal = false;

        for (const r of townCategoryRows) {
          const v = r[col.key];
          if (typeof v === "number" && Number.isFinite(v)) {
            sum += v;
            hasVal = true;
          }
        }

        if (hasVal) row[col.key] = sum;
      });

      return computeIncreaseFields(row);
    })();
    totalTownRow.order = townOrder++;
    townTableRows.push(totalTownRow);

    // --- DEBT & TRANSFERS OUT ---
    const debtRow = aggregateFromSections(
      debtServiceAndTransfers.sections || [],
      yearColumns,
      "Debt & Transfers Out"
    );
    debtRow.bold = false;
    debtRow.order = 0;
    const debtTableRows = [debtRow];

    // --- EDUCATION (canonical via fetchExpenditureSections("education")) ---
    // Aggregate education sections into a single row so the EDUCATION table is 1 row
    const educationRow = (() => {
      const agg = aggregateFromSections(
        education.sections || [],
        yearColumns,
        "Education"
      );

      // ensure increase fields computed consistently
      return computeIncreaseFields({ ...agg, label: "Education" });
    })();
    educationRow.order = 0;

    // --- TOTAL APPROPS/EXPENDITURES ---
    const totalAppropsRow = (() => {
      const row = { label: "APPROPRIATIONS/EXPENDITURES", bold: false };
      const yearCols = yearColumns.filter(
        (c) => !["label", "increase", "percentIncrease"].includes(c.key)
      );

      yearCols.forEach((col) => {
        const pieces = [totalTownRow, debtRow, educationRow];
        let sum = 0;
        let hasVal = false;

        for (const r of pieces) {
          const v = r[col.key];
          if (typeof v === "number" && Number.isFinite(v)) {
            sum += v;
            hasVal = true;
          }
        }

        if (hasVal) row[col.key] = sum;
      });

      return computeIncreaseFields(row);
    })();
    totalAppropsRow.order = 1;

    return {
      revenueRows: revenues,
      townRows: townTableRows,
      debtRows: debtTableRows,
      educationRows: [educationRow],
      totalAppropsRows: [totalAppropsRow],
    };
  }, []);

  const ensureYearsCached = useCallback(
    async (yearsToEnsure) => {
      const list = Array.isArray(yearsToEnsure) ? yearsToEnsure : [];
      if (!list.length) return;

      const missing = list.filter((y) => !yearCacheRef.current.get(y));
      if (!missing.length) return;

      setIsChartUpdating(true);
      try {
        for (const y of missing) {
          const computed = await computeSummaryForYear(y);
          yearCacheRef.current.set(y, computed);
        }
      } finally {
        setIsChartUpdating(false);
      }
    },
    [computeSummaryForYear]
  );

  // compute validYears (only when expanded)
  useEffect(() => {
    if (collapsed) return;

    if (!availableYears.length) {
      setValidYears([]);
      return;
    }

    let cancelled = false;

    (async () => {
      const nextValid = [];

      for (const y of availableYears) {
        if (cancelled) return;

        let computed = yearCacheRef.current.get(y);
        if (!computed) {
          try {
            computed = await computeSummaryForYear(y);
            yearCacheRef.current.set(y, computed);
          } catch (e) {
            console.warn(
              `[SummaryOfRevenuesAndExpenditures] Failed to evaluate year ${y}:`,
              e
            );
            continue;
          }
        }

        if (isYearComplete(computed)) nextValid.push(y);
      }

      if (cancelled) return;

      setValidYears(nextValid);

      if (nextValid.length) {
        setTableYear.initialize((prev) =>
          prev && nextValid.includes(prev)
            ? prev
            : nextValid[nextValid.length - 1]
        );
      } else {
        setTableYear.initialize((prev) => {
          const latest = availableYears[availableYears.length - 1];
          return prev && availableYears.includes(prev) ? prev : latest;
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [collapsed, availableYears, computeSummaryForYear, isYearComplete]);

  // Initialize year on first expand
  useEffect(() => {
    if (collapsed) return;
    if (!availableYears.length) return;

    setTableYear.initialize((prev) => {
      const source = yearOptionsToShow.length ? yearOptionsToShow : availableYears;
      const latest = source[source.length - 1];
      return prev && source.includes(prev) ? prev : latest;
    });
  }, [collapsed, availableYears, yearOptionsToShow]);

  // Warm cache for charts when any chart is opened
  const baseYearsForCharts = useMemo(
    () => (yearOptionsToShow.length ? yearOptionsToShow : availableYears),
    [yearOptionsToShow, availableYears]
  );

  useEffect(() => {
    if (collapsed) return;

    const wantsAnyChart =
      showApprovedTotalsChart || showActualTotalsChart || showDiffTotalsChart;

    if (!wantsAnyChart) return;

    const plus2Years = baseYearsForCharts.map((y) => y + 2);
    const toEnsure = Array.from(new Set([...baseYearsForCharts, ...plus2Years]));

    ensureYearsCached(toEnsure);
  }, [
    collapsed,
    showApprovedTotalsChart,
    showActualTotalsChart,
    showDiffTotalsChart,
    baseYearsForCharts,
    ensureYearsCached,
  ]);

  // START-year keyed fallback
  const fallbackYear = new Date().getFullYear();
  const currentYear = tableYear ?? fallbackYear;

  const { columns } = useMemo(() => buildRevenueColumns(currentYear), [currentYear]);

  // ensure pieColKey stays valid for the currently-built columns
  const pieColOptions = useMemo(() => {
    const numericCols = (columns || []).filter(
      (c) => c?.key && !["label", "increase", "percentIncrease"].includes(c.key)
    );

    return numericCols.map((c) => ({
      key: c.key,
      label: typeof c.label === "string" ? c.label : c.key,
    }));
  }, [columns]);

  useEffect(() => {
    if (!pieColOptions.length) return;

    setPieColKey.initialize((prev) => {
      const preferred =
        pieColOptions.find((o) => o.key === "y0_adopted")?.key ||
        pieColOptions[pieColOptions.length - 1].key;

      if (prev && pieColOptions.some((o) => o.key === prev)) return prev;
      return preferred;
    });
  }, [pieColOptions]);

  // Load selected year (cached first)
  useEffect(() => {
    if (collapsed) return;
    if (!tableYear) return;

    const cached = yearCacheRef.current.get(tableYear);
    if (cached) {
      setRevenueRows(cached.revenueRows);
      setTownRows(cached.townRows);
      setDebtRows(cached.debtRows);
      setEducationRows(cached.educationRows);
      setTotalAppropsRows(cached.totalAppropsRows);
      setLoading(false);
      setIsUpdating(false);
      return;
    }

    let cancelled = false;

    if (hasAnyData) setIsUpdating(true);
    else setLoading(true);

    (async () => {
      try {
        const computed = await computeSummaryForYear(tableYear);
        if (cancelled) return;

        yearCacheRef.current.set(tableYear, computed);

        setRevenueRows(computed.revenueRows);
        setTownRows(computed.townRows);
        setDebtRows(computed.debtRows);
        setEducationRows(computed.educationRows);
        setTotalAppropsRows(computed.totalAppropsRows);

        setLoading(false);
        setIsUpdating(false);
      } catch (err) {
        console.error(
          "[SummaryOfRevenuesAndExpenditures] Error building summary:",
          err
        );
        if (!cancelled) {
          setLoading(false);
          setIsUpdating(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [collapsed, tableYear, hasAnyData, computeSummaryForYear]);

  // Spending pie items
  const spendingPieItems = useMemo(() => {
    if (!hasAnyData) return [];
    if (!pieColKey) return [];

    const items = [];

    const addSlice = (label, value, keyHint) => {
      if (typeof value !== "number" || !Number.isFinite(value) || value === 0)
        return;

      const safeKey =
        String(keyHint || label || "")
          .trim()
          .replace(/\s+/g, "_")
          .replace(/[^a-zA-Z0-9_]/g, "") || `slice_${items.length + 1}`;

      items.push({
        key: safeKey,
        label,
        value,
        valueType: "currency",
        group: "other",
      });
    };

    // Town category rows: exclude section headers + TOTAL TOWN
    (townRows || []).forEach((r) => {
      if (!r || r.section) return;
      const lbl = String(r.label || "").trim();
      if (!lbl) return;
      if (lbl.toUpperCase().startsWith("TOTAL ")) return;
      addSlice(lbl, r[pieColKey], `town_${lbl}`);
    });

    // Debt & Transfers Out (single row)
    (debtRows || []).forEach((r) => {
      if (!r || r.section) return;
      const lbl = String(r.label || "Debt & Transfers Out").trim();
      if (!lbl) return;
      if (lbl.toUpperCase().startsWith("TOTAL ")) return;
      addSlice(lbl, r[pieColKey], `debt_${lbl}`);
    });

    // Education (single row)
    (educationRows || []).forEach((r) => {
      if (!r || r.section) return;
      const lbl = String(r.label || "Education").trim();
      if (!lbl) return;
      if (lbl.toUpperCase().startsWith("TOTAL ")) return;
      addSlice(lbl, r[pieColKey], `edu_${lbl}`);
    });

    items.sort((a, b) => (b.value || 0) - (a.value || 0));
    return items;
  }, [hasAnyData, pieColKey, townRows, debtRows, educationRows]);

  // --- Town totals charts models ---
  const townApprovedTotalsPoints = useMemo(() => {
    const baseYears = baseYearsForCharts;
    if (!baseYears.length) return [];

    const pts = [];
    baseYears.forEach((startYear) => {
      const computed = yearCacheRef.current.get(startYear);
      const totalRow = computed?.totalAppropsRows?.[0] || null;
      const v = totalRow?.y0_adopted;
      if (typeof v === "number" && Number.isFinite(v))
        pts.push({ year: startYear, total: v });
    });

    pts.sort((a, b) => a.year - b.year);
    return pts;
  }, [baseYearsForCharts]);

  const townActualTotalsPoints = useMemo(() => {
    const baseYears = baseYearsForCharts;
    if (!baseYears.length) return [];

    const pts = [];
    baseYears.forEach((fyStartYear) => {
      const anchorYear = fyStartYear + 2; // actual for FY Y is stored in (Y+2).y2_actual
      const computed = yearCacheRef.current.get(anchorYear);
      const totalRow = computed?.totalAppropsRows?.[0] || null;
      const v = totalRow?.y2_actual;
      if (typeof v === "number" && Number.isFinite(v))
        pts.push({ year: fyStartYear, total: v });
    });

    pts.sort((a, b) => a.year - b.year);
    return pts;
  }, [baseYearsForCharts]);

  const townApprovedTotalsChart = useMemo(() => {
    if (!townApprovedTotalsPoints.length) return { labels: [], series: [] };
    const labels = townApprovedTotalsPoints.map((p) =>
      fyLabelFromStartYear(p.year)
    );
    const data = townApprovedTotalsPoints.map((p) => p.total);

    return {
      labels,
      series: [
        {
          key: "totalApproved",
          label: "Total Approved Budget",
          data,
          group: "budget",
          defaultVisible: true,
          valueType: "currency",
        },
      ],
    };
  }, [townApprovedTotalsPoints]);

  const townActualTotalsChart = useMemo(() => {
    if (!townActualTotalsPoints.length) return { labels: [], series: [] };
    const labels = townActualTotalsPoints.map((p) =>
      fyLabelFromStartYear(p.year)
    );
    const data = townActualTotalsPoints.map((p) => p.total);

    return {
      labels,
      series: [
        {
          key: "totalActual",
          label: "Total Actual Spending",
          data,
          group: "spending",
          defaultVisible: true,
          valueType: "currency",
        },
      ],
    };
  }, [townActualTotalsPoints]);

  const townDiffTotalsChart = useMemo(() => {
    if (!townApprovedTotalsPoints.length || !townActualTotalsPoints.length) {
      return { labels: [], series: [], groups: [] };
    }

    const approvedByYear = new Map(
      townApprovedTotalsPoints.map((p) => [p.year, p.total])
    );
    const actualByYear = new Map(
      townActualTotalsPoints.map((p) => [p.year, p.total])
    );

    const commonYears = [...approvedByYear.keys()]
      .filter((y) => actualByYear.has(y))
      .sort((a, b) => a - b);

    if (!commonYears.length) return { labels: [], series: [], groups: [] };

    const labels = commonYears.map((y) => fyLabelFromStartYear(y));

    const series = [
      {
        key: "totalApproved",
        label: "Total Approved Budget",
        data: commonYears.map((y) => approvedByYear.get(y) ?? null),
        group: "budget",
        defaultVisible: true,
        valueType: "currency",
      },
      {
        key: "totalActual",
        label: "Total Actual Spending",
        data: commonYears.map((y) => actualByYear.get(y) ?? null),
        group: "spending",
        defaultVisible: true,
        valueType: "currency",
      },
    ];

    const groups = [
      {
        key: "totalBudget",
        label: "Total Budget",
        groupKey: "budget",
        seriesKeys: ["totalApproved", "totalActual"],
        defaultVisible: true,
      },
    ];

    return { labels, series, groups };
  }, [townApprovedTotalsPoints, townActualTotalsPoints]);

  return (
    <TableSearchScope>
    <div className="bg-base-100 shadow-md rounded-lg p-2 mt-6 border border-gray-300">
      <HeaderToggleButton reportNavigation title={TOWN_REPORTS[0].title} controlsId="town-combined-summary"
        isOpen={!collapsed} onToggle={() => setCollapsed(prev => !prev)} />

      {!collapsed && (
        <div id="town-combined-summary" className="bg-white p-4 space-y-3">
          {loading && !hasAnyData ? (
            <div className="text-sm text-gray-500">Loading summary data...</div>
          ) : !hasAnyData ? (
            <div className="text-sm text-gray-500">
              No summary data available for this fiscal year.
            </div>
          ) : (
            <>
              <div className="flex flex-wrap gap-2 items-center justify-between rounded border border-gray-200 bg-gray-100 px-2 py-1">
                <h3 className="text-lg font-semibold">REVENUES</h3>

                {yearOptionsToShow.length > 0 && (
                  <div className="ml-auto flex flex-wrap items-center justify-end gap-2 text-xs">
                    <SectionTableSearch />
                    <span className="font-semibold text-gray-700">
                      Current Year
                    </span>

                    <select
                      className="border rounded px-2 py-1 text-xs bg-white"
                      value={tableYear || ""}
                      onChange={(e) =>
                        setTableYear(
                          e.target.value ? Number(e.target.value) : tableYear
                        )
                      }
                    >
                      {yearOptionsToShow.map((y) => (
                        <option key={y} value={y}>
                          {fyLabelFromStartYear(y)}
                        </option>
                      ))}
                    </select>

                    {isUpdating && (
                      <span className="text-gray-500" aria-live="polite">
                        Updating…
                      </span>
                    )}
                  </div>
                )}
              </div>

              <CommonTable
                title={null}
                columns={columns}
                data={revenueRows}
                isEditing={false}
                headerColor="bg-[#f8cfa9]"
                showAggregateTotal={false}
                defaultSortField={null}
                defaultSortOrder="asc"
              />

              <CommonTable
                title="APPROPRIATIONS / EXPENDITURES – TOWN"
                columns={columns}
                data={townRows}
                isEditing={false}
                headerColor="bg-[#f8cfa9]"
                showAggregateTotal={false}
                defaultSortField={null}
                defaultSortOrder="asc"
              />

              <CommonTable
                title="DEBT & TRANSFERS OUT"
                columns={columns}
                data={debtRows}
                isEditing={false}
                headerColor="bg-[#f8cfa9]"
                showAggregateTotal={false}
                defaultSortField={null}
                defaultSortOrder="asc"
              />

              <CommonTable
                title="EDUCATION"
                columns={columns}
                data={educationRows}
                isEditing={false}
                headerColor="bg-[#f8cfa9]"
                showAggregateTotal={false}
                defaultSortField={null}
                defaultSortOrder="asc"
              />

              <CommonTable
                title="TOTAL APPROPRIATIONS/EXPENDITURES"
                columns={columns}
                data={totalAppropsRows}
                isEditing={false}
                headerColor="bg-[#f8cfa9]"
                showAggregateTotal={false}
                defaultSortField={null}
                defaultSortOrder="asc"
              />

              {/* Charts */}
              <div className="space-y-6 pt-2">
                {townApprovedTotalsChart.labels.length > 0 &&
                  townApprovedTotalsChart.series.length > 0 && (
                    <div className="space-y-1">
                      <HeaderToggleButton
                        isOpen={showApprovedTotalsChart}
                        onToggle={() =>
                          setShowApprovedTotalsChart((prev) => !prev)
                        }
                        title="Approved Budget"
                        ariaLabel="Toggle approved totals chart"
                        controlsId="town-totals-approved-chart"
                        rightSlot={
                          isChartUpdating ? (
                            <span
                              className="text-xs text-gray-500 pr-2"
                              aria-live="polite"
                            >
                              Updating…
                            </span>
                          ) : null
                        }
                      />

                      {showApprovedTotalsChart && (
                        <div id="town-totals-approved-chart">
                          <p className="text-xs text-gray-600 mb-1">
                            This chart shows total Town
                            appropriations/expenditures (Approved) by fiscal
                            year.
                          </p>

                          <LineChartWithToggles
                            labels={townApprovedTotalsChart.labels}
                            series={townApprovedTotalsChart.series}
                            showTotalWhenNoneSelected={true}
                            totalLabel="Total Approved Budget"
                            showSeriesToggles={true}
                            showChartTypeSelector={true}
                          />
                        </div>
                      )}
                    </div>
                  )}

                {townActualTotalsChart.labels.length > 0 &&
                  townActualTotalsChart.series.length > 0 && (
                    <div className="space-y-1">
                      <HeaderToggleButton
                        isOpen={showActualTotalsChart}
                        onToggle={() => setShowActualTotalsChart((prev) => !prev)}
                        title="Actual Spending"
                        ariaLabel="Toggle actual totals chart"
                        controlsId="town-totals-actual-chart"
                        rightSlot={
                          isChartUpdating ? (
                            <span
                              className="text-xs text-gray-500 pr-2"
                              aria-live="polite"
                            >
                              Updating…
                            </span>
                          ) : null
                        }
                      />

                      {showActualTotalsChart && (
                        <div id="town-totals-actual-chart">
                          <p className="text-xs text-gray-600 mb-1">
                            This chart shows total Town
                            appropriations/expenditures (Actual) by fiscal year.
                          </p>

                          <LineChartWithToggles
                            labels={townActualTotalsChart.labels}
                            series={townActualTotalsChart.series}
                            showTotalWhenNoneSelected={true}
                            totalLabel="Total Actual Spending"
                            showSeriesToggles={true}
                            showChartTypeSelector={true}
                          />
                        </div>
                      )}
                    </div>
                  )}

                {townDiffTotalsChart.labels.length > 0 &&
                  townDiffTotalsChart.series.length > 0 && (
                    <div className="space-y-1">
                      <HeaderToggleButton
                        isOpen={showDiffTotalsChart}
                        onToggle={() => setShowDiffTotalsChart((prev) => !prev)}
                        title="Actual Spending vs Approved Budget"
                        ariaLabel="Toggle actual vs approved totals chart"
                        controlsId="town-totals-diff-chart"
                        rightSlot={
                          isChartUpdating ? (
                            <span
                              className="text-xs text-gray-500 pr-2"
                              aria-live="polite"
                            >
                              Updating…
                            </span>
                          ) : null
                        }
                      />

                      {showDiffTotalsChart && (
                        <div id="town-totals-diff-chart">
                          <p className="text-xs text-gray-600 mb-1">
                            This chart compares total actual spending to the
                            total approved budget for the years where both are
                            available.
                          </p>

                          <LineChartWithToggles
                            labels={townDiffTotalsChart.labels}
                            series={townDiffTotalsChart.series}
                            groups={townDiffTotalsChart.groups}
                            showChartTypeSelector={true}
                          />
                        </div>
                      )}
                    </div>
                  )}

                {/* Breakdown of Spending */}
                <div className="space-y-1">
                  <HeaderToggleButton
                    isOpen={showSpendPie}
                    onToggle={() => setShowSpendPie((p) => !p)}
                    title="Breakdown of Spending"
                    ariaLabel="Toggle breakdown of spending"
                    controlsId="town-spending-pie"
                    rightSlot={
                      isChartUpdating ? (
                        <span
                          className="text-xs text-gray-500 pr-2"
                          aria-live="polite"
                        >
                          Updating…
                        </span>
                      ) : null
                    }
                  />

                  {showSpendPie && (
                    <div id="town-spending-pie">
                      <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
                        <p className="text-xs text-gray-600">
                          This pie shows how total appropriations/expenditures
                          are split across Town categories, Debt & Transfers
                          Out, and Education for the selected column.
                        </p>

                        {pieColOptions.length > 0 && (
                          <div className="flex flex-wrap items-center gap-2 text-xs">
                            <span className="font-semibold">Column</span>
                            <select
                              className="border rounded px-2 py-1 text-xs bg-white"
                              value={pieColKey || ""}
                              onChange={(e) => setPieColKey(e.target.value)}
                            >
                              {pieColOptions.map((o) => (
                                <option key={o.key} value={o.key}>
                                  {o.label}
                                </option>
                              ))}
                            </select>
                          </div>
                        )}
                      </div>

                      {spendingPieItems.length > 0 ? (
                        <PieChartWithToggles
                          title={null}
                          items={spendingPieItems}
                          percentOf="all"
                          showLegend={false}
                          showSeriesToggles={false}
                          showChartTypeSelector={false}
                          aggregateOthers={false}
                          includeZeroValues={true}
                          showLegendTable={true}
                          legendTablePlacement="right"
                          legendTableMaxHeight="420px"
                          enableHoverPop={true}
                          hoverPopOffset={12}
                          sliceOrder="valueDesc"
                        />
                      ) : (
                        <div className="text-sm text-gray-500">
                          No spending data available for the selected column.
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
    </TableSearchScope>
  );
}

export default SummaryOfRevenuesAndExpenditures;
